import React, { useEffect, useMemo, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import {
  FiAlertTriangle,
  FiClock,
  FiMessageSquare,
  FiPhone,
  FiUser,
} from "react-icons/fi";
import { api } from "../services/api";
import DataCard from "./ui/DataCard";
import StatusBadge from "./ui/StatusBadge";
import LoadingState from "./ui/LoadingState";
import AlertBanner from "./ui/AlertBanner";

const CHANNEL_ICONS = { App: "📱", Call: "📞", Email: "✉️", Whatsapp: "💬" };

const PriorityBadge = ({ priorityKey }) => (
  <span className={`support-priority support-priority-${priorityKey || "medium"}`}>
    {priorityKey ? priorityKey.charAt(0).toUpperCase() + priorityKey.slice(1) : "Medium"}
  </span>
);

const formatAge = (hours) => {
  if (hours < 1) return "< 1h ago";
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h ago`;
};

const CustomerSupport = ({ onRefreshSummary }) => {
  const [tickets, setTickets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [complaintCategories, setComplaintCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterChannel, setFilterChannel] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    client_user_id: "",
    subject: "",
    description: "",
    priority: "medium",
    channel: "call",
    complaint_category_id: "",
    agent_code: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ticketData, agentData, callData, userData] = await Promise.all([
        api.getTickets(),
        api.getAgents(),
        api.getSupportCalls(),
        api.getUsers(),
      ]);
      setTickets(ticketData);
      setAgents(agentData);
      setComplaintCategories(callData.complaintCategories || []);
      setUsers(userData);
      if (ticketData.length && !selectedId) setSelectedId(ticketData[0].id);
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return tickets.filter((t) => {
      const matchSearch =
        !q ||
        t.id.toLowerCase().includes(q) ||
        t.user.toLowerCase().includes(q) ||
        t.subject.toLowerCase().includes(q) ||
        t.phone.includes(q);
      const matchStatus = !filterStatus || t.status === filterStatus;
      const matchPriority = !filterPriority || t.priorityKey === filterPriority;
      const matchChannel = !filterChannel || t.channelKey === filterChannel;
      return matchSearch && matchStatus && matchPriority && matchChannel;
    });
  }, [tickets, search, filterStatus, filterPriority, filterChannel]);

  const selected = tickets.find((t) => t.id === selectedId) || filtered[0];

  const runAction = async (id, action, extra = {}) => {
    setActionLoading(`${id}-${action}`);
    setFeedback(null);
    try {
      const result = await api.updateTicket(id, action, extra);
      setTickets((prev) =>
        prev.map((t) => {
          if (t.id !== id) return t;
          const entry = result.historyEntry;
          const history = entry
            ? [...t.history, typeof entry === "string" ? { text: entry, time: "", action: "" } : entry]
            : t.history;
          return { ...t, ...result, history };
        })
      );
      setFeedback({ type: "success", message: `Ticket ${id} updated.` });
      if (action === "add_note") setNoteText("");
      onRefreshSummary?.();
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreate = async () => {
    if (!createForm.client_user_id || !createForm.subject) {
      setFeedback({ type: "warning", message: "Customer and subject are required." });
      return;
    }
    setActionLoading("create-ticket");
    try {
      const result = await api.createTicket(createForm);
      setShowCreate(false);
      setFeedback({ type: "success", message: `Ticket ${result.id} created.` });
      await loadData();
      setSelectedId(result.id);
      onRefreshSummary?.();
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <LoadingState message="Loading ticket desk..." />;

  return (
    <div>
      {feedback && (
        <AlertBanner type={feedback.type} message={feedback.message} onClose={() => setFeedback(null)} />
      )}

      <div className="support-toolbar">
        <div className="support-toolbar-filters">
          <input
            className="billr-input"
            placeholder="Search ticket, customer, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="billr-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="Open">Open</option>
            <option value="In Progress">In Progress</option>
            <option value="Escalated">Escalated</option>
            <option value="Closed">Closed</option>
          </select>
          <select className="billr-select" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
            <option value="">All priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select className="billr-select" value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)}>
            <option value="">All channels</option>
            <option value="app">App</option>
            <option value="call">Call</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </div>
        <button className="btn-billr btn-billr-primary" onClick={() => setShowCreate(true)}>
          + New Ticket
        </button>
      </div>

      <div className="support-desk">
        <div className="support-queue">
          <div className="support-queue-header">
            <span>Queue</span>
            <span className="support-queue-count">{filtered.length}</span>
          </div>
          <div className="support-queue-list">
            {filtered.length === 0 && (
              <div className="support-empty">No tickets match your filters.</div>
            )}
            {filtered.map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                className={`support-queue-item${selected?.id === ticket.id ? " active" : ""}${ticket.slaBreached ? " sla-risk" : ""}`}
                onClick={() => setSelectedId(ticket.id)}
              >
                <div className="support-queue-item-top">
                  <code>{ticket.id}</code>
                  <PriorityBadge priorityKey={ticket.priorityKey} />
                </div>
                <div className="support-queue-subject">{ticket.subject}</div>
                <div className="support-queue-meta">
                  <span>{ticket.user}</span>
                  <StatusBadge status={ticket.statusKey} label={ticket.status} />
                </div>
                {ticket.slaBreached && (
                  <div className="support-sla-tag">
                    <FiAlertTriangle size={12} /> SLA breach
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {selected ? (
          <div className="support-detail">
            <div className="support-detail-header">
              <div>
                <div className="support-detail-id">
                  <code>{selected.id}</code>
                  <StatusBadge status={selected.statusKey} label={selected.status} />
                  <PriorityBadge priorityKey={selected.priorityKey} />
                  {selected.slaBreached && (
                    <span className="support-sla-tag inline">
                      <FiAlertTriangle size={12} /> Over 24h
                    </span>
                  )}
                </div>
                <h3 className="support-detail-title">{selected.subject}</h3>
                <div className="support-detail-chips">
                  <span>{CHANNEL_ICONS[selected.channel] || "📋"} {selected.channel}</span>
                  <span>{selected.complaintType}</span>
                  <span><FiClock size={13} /> {formatAge(selected.ageHours)}</span>
                </div>
              </div>
              <div className="support-detail-actions">
                {selected.statusKey !== "closed" && (
                  <>
                    <select
                      className="billr-select"
                      value={selected.assignedTo || ""}
                      disabled={!!actionLoading}
                      onChange={(e) => e.target.value && runAction(selected.id, "assign", { agent_code: e.target.value })}
                    >
                      <option value="">Assign agent</option>
                      {agents.map((a) => (
                        <option key={a.staff_code} value={a.staff_code}>
                          {a.staff_code} — {a.full_name}
                        </option>
                      ))}
                    </select>
                    <select
                      className="billr-select"
                      value={selected.priorityKey}
                      disabled={!!actionLoading}
                      onChange={(e) => runAction(selected.id, "set_priority", { priority: e.target.value })}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                    <button
                      className="btn-billr btn-billr-outline btn-billr-sm"
                      disabled={!!actionLoading}
                      onClick={() => runAction(selected.id, "escalate", { reason: "Escalated from ticket desk" })}
                    >
                      Escalate
                    </button>
                    <button
                      className="btn-billr btn-billr-primary btn-billr-sm"
                      disabled={!!actionLoading}
                      onClick={() => runAction(selected.id, "close", { resolution: "Resolved via support desk" })}
                    >
                      Close
                    </button>
                  </>
                )}
                {selected.statusKey === "closed" && (
                  <button
                    className="btn-billr btn-billr-outline btn-billr-sm"
                    disabled={!!actionLoading}
                    onClick={() => runAction(selected.id, "reopen")}
                  >
                    Reopen
                  </button>
                )}
              </div>
            </div>

            <Row className="g-3 mb-3">
              <Col md={4}>
                <div className="support-info-card">
                  <div className="support-info-label"><FiUser size={14} /> Customer</div>
                  <div className="support-info-value">{selected.user}</div>
                  <div className="support-info-sub">{selected.phone}</div>
                  {selected.email && <div className="support-info-sub">{selected.email}</div>}
                  <div className="support-info-tags">
                    <StatusBadge status={selected.kycStatus} label={`KYC: ${selected.kycStatus}`} />
                    <StatusBadge status={selected.clientStatus?.toLowerCase()} label={selected.clientStatus} />
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="support-info-card">
                  <div className="support-info-label">Assigned agent</div>
                  <div className="support-info-value">{selected.assignedTo || "Unassigned"}</div>
                  {selected.agentName && <div className="support-info-sub">{selected.agentName}</div>}
                  <div className="support-info-sub mt-2">Created {selected.created}</div>
                  <div className="support-info-sub">Updated {selected.updated}</div>
                  {selected.closedAt && <div className="support-info-sub">Closed {selected.closedAt}</div>}
                </div>
              </Col>
              <Col md={4}>
                <div className="support-info-card">
                  <div className="support-info-label"><FiPhone size={14} /> Linked calls</div>
                  {selected.linkedCalls?.length ? (
                    selected.linkedCalls.map((c) => (
                      <div key={c.call_ref} className="support-linked-call">
                        <code>{c.call_ref}</code>
                        <StatusBadge status={c.call_status} label={c.call_status.replace(/_/g, " ")} />
                        <span className="text-muted small">{c.call_time}</span>
                      </div>
                    ))
                  ) : (
                    <div className="support-info-sub">No calls linked to this ticket.</div>
                  )}
                </div>
              </Col>
            </Row>

            <DataCard title="Issue description" padded>
              <p className="support-description mb-0">{selected.description}</p>
            </DataCard>

            <DataCard title="Internal notes" padded className="mt-3">
              <div className="support-note-form">
                <textarea
                  className="billr-input"
                  rows={2}
                  placeholder="Add an internal note for the team..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
                <button
                  className="btn-billr btn-billr-primary btn-billr-sm"
                  disabled={!noteText.trim() || !!actionLoading}
                  onClick={() => runAction(selected.id, "add_note", { note: noteText.trim() })}
                >
                  <FiMessageSquare size={14} /> Add note
                </button>
              </div>
            </DataCard>

            <DataCard title="Activity timeline" padded className="mt-3">
              <div className="support-timeline">
                {[...(selected.history || [])].reverse().map((entry, idx) => (
                  <div key={idx} className="support-timeline-item">
                    <div className="support-timeline-dot" />
                    <div className="support-timeline-content">
                      <div className="support-timeline-time">{entry.time || entry.text?.split(" — ")[0]}</div>
                      <div className="support-timeline-action">{entry.action || entry.text}</div>
                      {entry.message && <div className="support-timeline-msg">{entry.message}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </DataCard>
          </div>
        ) : (
          <div className="support-detail support-empty">Select a ticket from the queue.</div>
        )}
      </div>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create support ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <label className="small fw-semibold text-muted">Customer</label>
              <select
                className="billr-select"
                value={createForm.client_user_id}
                onChange={(e) => setCreateForm({ ...createForm, client_user_id: e.target.value })}
              >
                <option value="">Select customer</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.mobile})</option>
                ))}
              </select>
            </Col>
            <Col md={6}>
              <label className="small fw-semibold text-muted">Complaint type</label>
              <select
                className="billr-select"
                value={createForm.complaint_category_id}
                onChange={(e) => setCreateForm({ ...createForm, complaint_category_id: e.target.value })}
              >
                <option value="">General</option>
                {complaintCategories.map((c) => (
                  <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
                ))}
              </select>
            </Col>
            <Col md={12}>
              <label className="small fw-semibold text-muted">Subject</label>
              <input
                className="billr-input"
                value={createForm.subject}
                onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })}
              />
            </Col>
            <Col md={12}>
              <label className="small fw-semibold text-muted">Description</label>
              <textarea
                className="billr-input"
                rows={3}
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
            </Col>
            <Col md={4}>
              <label className="small fw-semibold text-muted">Priority</label>
              <select
                className="billr-select"
                value={createForm.priority}
                onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </Col>
            <Col md={4}>
              <label className="small fw-semibold text-muted">Channel</label>
              <select
                className="billr-select"
                value={createForm.channel}
                onChange={(e) => setCreateForm({ ...createForm, channel: e.target.value })}
              >
                <option value="call">Call</option>
                <option value="app">App</option>
                <option value="email">Email</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </Col>
            <Col md={4}>
              <label className="small fw-semibold text-muted">Assign to</label>
              <select
                className="billr-select"
                value={createForm.agent_code}
                onChange={(e) => setCreateForm({ ...createForm, agent_code: e.target.value })}
              >
                <option value="">Unassigned</option>
                {agents.map((a) => (
                  <option key={a.staff_code} value={a.staff_code}>{a.staff_code}</option>
                ))}
              </select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn-billr btn-billr-outline" onClick={() => setShowCreate(false)}>Cancel</button>
          <button
            className="btn-billr btn-billr-primary"
            disabled={actionLoading === "create-ticket"}
            onClick={handleCreate}
          >
            Create ticket
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CustomerSupport;
